// Minimal hand-rolled single/multi-page PDF writer — no dependency, since
// CLAUDE.md prefers copying a small utility over installing a package (e.g.
// jsPDF) for a footprint this small. Only does what a bank-statement-style
// export needs: left-aligned columns of text on a US Letter page, using the
// standard Helvetica/Helvetica-Bold fonts (built into every PDF reader, so
// nothing needs to be embedded). Produces a real, valid application/pdf
// Blob — not a text file with a .pdf extension.
const encoder = new TextEncoder();
const PAGE_W = 612, PAGE_H = 792, MARGIN = 40, ROW_H = 14;

function esc(str) {
  return String(str).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

// Standard-14 fonts only support Latin-1/WinAnsi text — sanitize the odd
// character (en/em dash, arrows, curly quotes) that shows up in this app's
// demo copy rather than embedding a font just for a few glyphs.
function ascii(str) {
  return String(str)
    .replace(/[–—]/g, '-')
    .replace(/→/g, '->')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^\x20-\x7E]/g, '?');
}

function text(ops, x, y, size, str, bold) {
  ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${x} ${y} Td (${esc(ascii(str))}) Tj ET`);
}
function hline(ops, y) {
  ops.push(`${MARGIN} ${y} m ${PAGE_W - MARGIN} ${y} l S`);
}

const COLS = [
  { key: 'date', x: 0, label: 'Date' },
  { key: 'desc', x: 62, label: 'Description' },
  { key: 'wallet', x: 250, label: 'Account' },
  { key: 'amount', x: 330, label: 'Amount' },
  { key: 'hash', x: 425, label: 'Tx hash' }
];

function columnHeader(ops, y) {
  COLS.forEach((c) => text(ops, MARGIN + c.x, y, 8, c.label, true));
  hline(ops, y - 4);
}

// Builds one page's content stream, returning the y position of the row
// area's top so rows can start being placed right after the header.
function startPage(isFirst, subtitle, generatedLine) {
  const ops = [];
  let y = PAGE_H - MARGIN;
  if (isFirst) {
    text(ops, MARGIN, y, 20, 'TYNTI', true);
    y -= 20;
    text(ops, MARGIN, y, 11, subtitle, false);
    y -= 16;
    text(ops, MARGIN, y, 8, generatedLine, false);
    y -= 8;
    hline(ops, y);
    y -= 20;
  } else {
    text(ops, MARGIN, y, 12, 'TYNTI — continued', true);
    y -= 24;
  }
  columnHeader(ops, y);
  y -= ROW_H;
  return { ops, y };
}

export function buildStatementPdf({ subtitle, generatedLine, rows }) {
  const pages = [];
  let { ops, y } = startPage(true, subtitle, generatedLine);

  const body = rows.length ? rows : [{ date: '', desc: 'No transactions match these filters.', wallet: '', amount: '', hash: '' }];
  body.forEach((r) => {
    if (y < MARGIN + ROW_H) {
      pages.push(ops);
      ({ ops, y } = startPage(false));
    }
    COLS.forEach((c) => text(ops, MARGIN + c.x, y, 8, r[c.key] ?? '', false));
    y -= ROW_H;
  });
  pages.push(ops);

  return assemblePdf(pages);
}

function assemblePdf(pages) {
  const chunks = [];
  let offset = 0;
  const objOffsets = {};

  function push(str) {
    const bytes = encoder.encode(str);
    const start = offset;
    chunks.push(bytes);
    offset += bytes.length;
    return start;
  }

  push('%PDF-1.4\n');

  const fontF1Id = 3, fontF2Id = 4;
  let nextId = 5;
  const pageIds = [];
  const contentIds = [];
  pages.forEach(() => { pageIds.push(nextId++); contentIds.push(nextId++); });

  objOffsets[1] = push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
  const kids = pageIds.map((id) => `${id} 0 R`).join(' ');
  objOffsets[2] = push(`2 0 obj\n<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>\nendobj\n`);
  objOffsets[fontF1Id] = push(`${fontF1Id} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`);
  objOffsets[fontF2Id] = push(`${fontF2Id} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`);

  pages.forEach((ops, i) => {
    const content = ops.join('\n');
    const contentBytes = encoder.encode(content);
    objOffsets[contentIds[i]] = push(`${contentIds[i]} 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n${content}\nendstream\nendobj\n`);
    objOffsets[pageIds[i]] = push(
      `${pageIds[i]} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] ` +
      `/Resources << /Font << /F1 ${fontF1Id} 0 R /F2 ${fontF2Id} 0 R >> >> /Contents ${contentIds[i]} 0 R >>\nendobj\n`
    );
  });

  const totalObjs = nextId - 1;
  const xrefStart = offset;
  let xref = `xref\n0 ${totalObjs + 1}\n0000000000 65535 f \n`;
  for (let id = 1; id <= totalObjs; id++) {
    xref += String(objOffsets[id] || 0).padStart(10, '0') + ' 00000 n \n';
  }
  push(xref);
  push(`trailer\n<< /Size ${totalObjs + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`);

  return new Blob(chunks, { type: 'application/pdf' });
}
